'use client';

import { useInbox } from '@trycourier/react-inbox';
import { IInboxMessagePreview } from '@trycourier/react-provider';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type MergedInboxContextValue = {
  messages: IInboxMessagePreview[];
  fetchMessages: (params?: { params: { limit: number } }) => void;
  markMessageRead: (messageId: string) => Promise<void>;
  markMessageArchived: (messageId: string) => Promise<void>;
};

const MergedInboxContext = createContext<MergedInboxContextValue | null>(null);

export const useMergedInbox = (): MergedInboxContextValue => {
  const context = useContext(MergedInboxContext);

  if (!context) {
    throw new Error('useMergedInbox must be used within a MergedInboxProvider');
  }

  return context;
};

type SourceData = {
  messages: IInboxMessagePreview[];
  fetchMessages: (params?: { params: { limit: number } }) => void;
  markMessageRead: (messageId: string) => Promise<void>;
  markMessageArchived: (messageId: string) => Promise<void>;
};

const SourceRegistryContext = createContext<{
  register: (id: string, data: SourceData) => void;
  unregister: (id: string) => void;
} | null>(null);

const buildMessageKey = (messages: IInboxMessagePreview[]): string =>
  messages.map((m) => m.messageId).sort().join(',');

export const MergedInboxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const sourcesRef = useRef<Map<string, SourceData>>(new Map());
  const mergedKeyRef = useRef('');
  const [mergedMessages, setMergedMessages] = useState<IInboxMessagePreview[]>([]);

  const rebuild = useCallback(() => {
    const allMessages: IInboxMessagePreview[] = [];
    const seenIds = new Set<string>();

    for (const source of sourcesRef.current.values()) {
      for (const msg of source.messages) {
        if (!seenIds.has(msg.messageId)) {
          seenIds.add(msg.messageId);
          allMessages.push(msg);
        }
      }
    }

    const key = buildMessageKey(allMessages);

    if (key === mergedKeyRef.current) {
      return;
    }

    mergedKeyRef.current = key;
    allMessages.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
    setMergedMessages(allMessages);
  }, []);

  const register = useCallback((id: string, data: SourceData) => {
    sourcesRef.current.set(id, data);
    rebuild();
  }, [rebuild]);

  const unregister = useCallback((id: string) => {
    sourcesRef.current.delete(id);
    rebuild();
  }, [rebuild]);

  const fetchMessages = useCallback((params?: { params: { limit: number } }) => {
    for (const source of sourcesRef.current.values()) {
      source.fetchMessages(params);
    }
  }, []);

  const markMessageRead = useCallback(async (messageId: string) => {
    for (const source of sourcesRef.current.values()) {
      if (source.messages.some((m) => m.messageId === messageId)) {
        await source.markMessageRead(messageId);

        return;
      }
    }
  }, []);

  const markMessageArchived = useCallback(async (messageId: string) => {
    for (const source of sourcesRef.current.values()) {
      if (source.messages.some((m) => m.messageId === messageId)) {
        await source.markMessageArchived(messageId);

        return;
      }
    }
  }, []);

  const registryValue = useMemo(() => ({ register, unregister }), [register, unregister]);

  const value = useMemo(() => ({
    messages: mergedMessages,
    fetchMessages,
    markMessageRead,
    markMessageArchived,
  }), [mergedMessages, fetchMessages, markMessageRead, markMessageArchived]);

  return (
    <MergedInboxContext.Provider value={value}>
      <SourceRegistryContext.Provider value={registryValue}>
        {children}
      </SourceRegistryContext.Provider>
    </MergedInboxContext.Provider>
  );
};

export const InboxCollector: React.FC<{ sourceId: string }> = ({ sourceId }) => {
  const registry = useContext(SourceRegistryContext);
  const { messages, fetchMessages, markMessageRead, markMessageArchived } = useInbox();

  const messageKey = useMemo(() => buildMessageKey(messages ?? []), [messages]);

  const callbacksRef = useRef({ fetchMessages, markMessageRead, markMessageArchived });
  callbacksRef.current = { fetchMessages, markMessageRead, markMessageArchived };

  useEffect(() => {
    if (!registry) {
      return;
    }

    registry.register(sourceId, {
      messages: messages ?? [],
      fetchMessages: (...args) => callbacksRef.current.fetchMessages(...args),
      markMessageRead: (...args) => callbacksRef.current.markMessageRead(...args),
      markMessageArchived: (...args) => callbacksRef.current.markMessageArchived(...args),
    });

    return () => {
      registry.unregister(sourceId);
    };
  }, [sourceId, registry, messageKey]);

  return null;
};
